// Servicio de llamadas del panel (spec panel-atencion §4). IO PostgREST +
// Realtime, sin React. Solo lectura: las policies del panel no permiten escribir.

import { supabase } from '../lib/supabase';
import { _rangoDia } from '../lib/llamadas/fechas';
import { parseParteOdoo, type ParteOdoo } from '../lib/llamadas/parte';
import { TIPO_PARTE_CREADO } from '../lib/llamadas/presentacion';
import type { LlamadaEventoPanel, LlamadaFeedRow, LlamadaRow } from '../types/llamadas';

export type EventoLlamada = 'INSERT' | 'UPDATE';

// Llamadas cuyo `inicio` cae dentro del día `dia` (YYYY-MM-DD, Europe/Madrid),
// orden inicio DESC. Filas con inicio null quedan fuera por el propio filtro.
// Columnas del feed (v2, carga perezosa): sin transcripcion (hasta ~10k líneas
// por llamada; el detalle la pide con fetchLlamada) ni datos_extra (jsonb sin
// cota que la UI no usa; AR-P2-1) — ambas llegan null en las filas del feed.
// Se embebe la razón social del cliente vinculado (relación FK id_cliente).
const COLUMNAS_FEED =
  'id, call_id, estado, direccion, telefono_origen, telefono_destino, inicio, fin, ' +
  'duracion_s, desconexion, resumen, sentimiento, exitosa, buzon, ' +
  'motivo, rama, cliente_id_odoo, id_cliente, resultado, creado_en, actualizado_en, ' +
  'cliente:clientes_replica(razon_social)';

// Tamaño de página. PostgREST corta en 1.000 filas por defecto (medido en
// producción) y devuelve un `content-range` que, si nadie lo mira, convierte
// una lista truncada en cifras falsas sin ningún error.
const TAM_PAGINA = 1000;
// Tope duro: 50 páginas = 50.000 llamadas. No es paranoia — con inserciones
// concurrentes el `count` se mueve, y un bucle sin cota es un cuelgue del
// navegador.
const MAX_PAGINAS = 50;

// Trae TODAS las filas del rango, paginando. Reglas, por orden de importancia:
//
//   1. Orden TOTAL: `inicio DESC` + `id`. Sin el desempate, el orden entre filas
//      con el mismo `inicio` lo decide Postgres y puede variar entre peticiones;
//      con paginación por offset eso significa filas repetidas en una página y
//      ausentes en otra. Improbable con llamadas de un bot, pero el fallo sería
//      silencioso, que es justo el pecado que este cambio viene a corregir.
//   2. El offset avanza por filas RECIBIDAS, no por `TAM_PAGINA`: si el servidor
//      tuviera un `max-rows` menor, avanzar de mil en mil dejaría huecos.
//   3. Dedup por `id` al concatenar. Una inserción durante la paginación
//      desplaza el offset; con `inicio DESC` las nuevas entran por la cabeza,
//      que es el peor caso. Esto elimina la mitad duplicada del problema; la
//      otra —una fila desplazada fuera de la ventana— queda como riesgo
//      asumido: una llamada de menos en una carrera de milisegundos, frente a
//      las 1.500 de menos que hoy se pierden en silencio.
// `tam` es parametrizable SOLO para poder probar el escenario que motiva el
// criterio de parada: un servidor que recorta por debajo del tamaño de página.
// Con el valor por defecto no se puede distinguir un criterio correcto de uno
// incorrecto, porque TAM_PAGINA coincide con el corte real de PostgREST.
export async function _paginarFeed(
  desde: string,
  hasta: string,
  evt: string,
  mensaje: string,
  tam: number = TAM_PAGINA
) {
  const vistas = new Set<string>();
  const acumulado: Omit<
    LlamadaFeedRow,
    | 'transcripcion'
    | 'datos_extra'
    | 'equipo_id_odoo'
    | 'id_equipo'
    | 'equipo_descrito'
    | 'equipo_origen'
    | 'equipo'
  >[] = [];
  let offset = 0;
  let total: number | null = null;

  for (let pagina = 0; pagina < MAX_PAGINAS; pagina++) {
    // El `count` se pide solo en la primera página: es lo que da el criterio de
    // parada fiable. Pedirlo en todas costaría un COUNT por página.
    const { data, error, count } = await supabase
      .from('llamadas')
      .select(COLUMNAS_FEED, pagina === 0 ? { count: 'exact' } : {})
      .gte('inicio', desde)
      .lt('inicio', hasta)
      .order('inicio', { ascending: false })
      .order('id')
      .range(offset, offset + tam - 1);

    if (error) {
      console.error(JSON.stringify({ evt, code: error.code, message: error.message }));
      throw new Error(`${mensaje}: ${error.message}`);
    }

    const filas = (data ?? []) as unknown as Omit<
      LlamadaFeedRow,
      | 'transcripcion'
      | 'datos_extra'
      | 'equipo_id_odoo'
      | 'id_equipo'
      | 'equipo_descrito'
      | 'equipo_origen'
      | 'equipo'
    >[];
    if (pagina === 0) total = count ?? null;
    if (filas.length === 0) break;

    for (const fila of filas) {
      if (vistas.has(fila.id)) continue;
      vistas.add(fila.id);
      acumulado.push(fila);
    }

    offset += filas.length;

    // Se para por el TOTAL, no por "esta página vino corta". Parar con
    // `filas.length < TAM_PAGINA` parece equivalente y no lo es: si el servidor
    // recortase por debajo de nuestro tamaño de página —justo el escenario que
    // la regla del offset por filas recibidas contempla— la primera página
    // vendría corta y romperíamos el bucle con la mitad de las filas. Volvería
    // la truncación silenciosa que este helper existe para eliminar.
    if (total !== null && offset >= total) break;

    // Salir por el tope de páginas es la misma truncación silenciosa que este
    // helper existe para matar, solo que con otro techo: 50 páginas son 50.000
    // filas, que con el rango de tres años son 46 llamadas al día. Antes se
    // devolvían las 50.000 primeras como si fueran todas. Ahora revienta, que
    // es lo correcto: mejor un error que un cuadro con cifras bajas creíbles.
    if (pagina === MAX_PAGINAS - 1) {
      console.error(JSON.stringify({ evt, code: 'max_paginas', offset, total }));
      throw new Error(
        `El periodo tiene más de ${offset.toLocaleString('es-ES')} llamadas y no cabe en ` +
          'una sola consulta. Acorta el rango de fechas.'
      );
    }
  }

  return acumulado.map((fila) => ({
    ...fila,
    transcripcion: null,
    datos_extra: null,
    // Los campos del equipo son de detalle y el feed no los pide. Se ponen a
    // null EXPLÍCITAMENTE, como la transcripción. Lo que protege de verdad es
    // el TIPO —`LlamadaFeedRow` los declara, así que omitirlos no compila—; a
    // `estadoEquipo` le da igual `null` que `undefined` (comprobado). O sea que
    // esto es coherencia con el patrón de al lado, no una guarda de ejecución.
    equipo_id_odoo: null,
    id_equipo: null,
    equipo_descrito: null,
    equipo_origen: null,
    equipo: null,
    cliente: fila.cliente ?? null,
  }));
}

export async function fetchLlamadasDelDia(dia: string): Promise<LlamadaFeedRow[]> {
  const { desde, hasta } = _rangoDia(dia);
  // Un día con más de 1.000 llamadas es improbable hoy, pero el bug es el mismo
  // que el del cuadro y el arreglo sale gratis al compartir el helper.
  return _paginarFeed(desde, hasta, 'fetch_llamadas_failed', 'No pude cargar las llamadas del día');
}

// Llamadas cuyo `inicio` cae en [desde, hasta) — ISO UTC de resolverPeriodo —
// orden inicio DESC. Mismas columnas del feed (spec cuadro-mando §4).
export async function fetchLlamadasRango(desde: string, hasta: string): Promise<LlamadaFeedRow[]> {
  return _paginarFeed(
    desde,
    hasta,
    'fetch_rango_failed',
    'No pude cargar las llamadas del periodo'
  );
}

// Cuántas llamadas hay en el rango SIN traerlas: `head: true` devuelve solo la
// cabecera con el count. Sirve para avisar ANTES de descargar un periodo enorme;
// cortar a mitad de la paginación ya habría bajado las primeras mil.
export async function contarLlamadasRango(desde: string, hasta: string): Promise<number> {
  const { count, error } = await supabase
    .from('llamadas')
    .select('id', { count: 'exact', head: true })
    .gte('inicio', desde)
    .lt('inicio', hasta);

  if (error) {
    console.error(
      JSON.stringify({ evt: 'contar_rango_failed', code: error.code, message: error.message })
    );
    throw new Error(`No pude contar las llamadas del periodo: ${error.message}`);
  }
  return count ?? 0;
}

// Fila completa de una llamada (CON transcripción) para el detalle; null si no
// existe. datos_extra sigue fuera (la UI no lo usa).
const COLUMNAS_DETALLE =
  'id, call_id, estado, direccion, telefono_origen, telefono_destino, inicio, fin, ' +
  'duracion_s, transcripcion, desconexion, resumen, sentimiento, exitosa, buzon, ' +
  'motivo, rama, cliente_id_odoo, id_cliente, resultado, creado_en, actualizado_en, ' +
  'equipo_id_odoo, id_equipo, equipo_descrito, equipo_origen, ' +
  'equipo:equipos_replica(referencia, tipo)';

export async function fetchLlamada(id: string): Promise<LlamadaRow | null> {
  const { data, error } = await supabase
    .from('llamadas')
    .select(COLUMNAS_DETALLE)
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error(
      JSON.stringify({ evt: 'fetch_llamada_failed', code: error.code, message: error.message })
    );
    throw new Error(`No pude cargar la llamada: ${error.message}`);
  }
  if (!data) return null;
  return { ...(data as unknown as Omit<LlamadaRow, 'datos_extra'>), datos_extra: null };
}

// --- Bitácora de la llamada (log-resolucion-odoo) ---

// Sin `datos` a propósito: en los transfer_* guarda el payload íntegro de Retell
// (transcripción incluida) y el panel no lo usa. Ver LlamadaEventoPanel.
const COLUMNAS_EVENTO = 'id, call_id, id_llamada, tipo, destino, motivo, ts, creado_en';

// Eventos de una llamada por su `call_id`, NO por el uuid: un evento puede
// existir con id_llamada null si llegó antes que su llamada (la RPC lo tolera y
// lo enlaza al reenviarse). Orden ts ASC con desempate por creado_en: los hitos
// `llamada_finalizada` y `llamada_analizada` comparten `end_timestamp`, así que
// sin el segundo criterio su orden lo decidiría Postgres.
export async function fetchEventosLlamada(callId: string): Promise<LlamadaEventoPanel[]> {
  const { data, error } = await supabase
    .from('llamada_eventos')
    .select(COLUMNAS_EVENTO)
    .eq('call_id', callId)
    .order('ts', { ascending: true })
    .order('creado_en', { ascending: true });

  if (error) {
    console.error(
      JSON.stringify({ evt: 'fetch_eventos_failed', code: error.code, message: error.message })
    );
    throw new Error(`No pude cargar la bitácora de la llamada: ${error.message}`);
  }
  return (data ?? []) as unknown as LlamadaEventoPanel[];
}

// El parte creado en Odoo de una llamada, desde el `datos` de su último hito
// `parte_creado`. Es la única lectura de `datos` que PIDE el panel, y va filtrada
// por tipo: el `datos` de un transfer_* lleva el payload crudo de Retell. Ojo, que
// esto no es una barrera: la RLS de llamada_eventos deja leer todas sus columnas a
// cualquier autenticado, y Realtime entrega la fila entera por el WebSocket
// (`_sinDatos` solo evita guardarla). La barrera de verdad es la deuda SD16.
// Un fallo o un `datos` ilegible dan null: la línea de tiempo ya dice que el
// parte existe, la tarjeta solo lo detalla.
export async function fetchParteLlamada(callId: string): Promise<ParteOdoo | null> {
  const { data, error } = await supabase
    .from('llamada_eventos')
    .select('datos')
    .eq('call_id', callId)
    .eq('tipo', TIPO_PARTE_CREADO)
    .order('ts', { ascending: false })
    .order('creado_en', { ascending: false })
    .limit(1);

  if (error) {
    console.error(
      JSON.stringify({ evt: 'fetch_parte_failed', code: error.code, message: error.message })
    );
    return null;
  }
  const fila = (data ?? [])[0] as { datos?: unknown } | undefined;
  return fila ? parseParteOdoo(fila.datos) : null;
}

// Descarta `datos` de una fila de bitácora venga de donde venga. Exportada para
// poder probarla: es la única defensa del camino Realtime.
export function _sinDatos(fila: Record<string, unknown>): LlamadaEventoPanel {
  const copia = { ...fila };
  delete copia.datos;
  return copia as unknown as LlamadaEventoPanel;
}

// Suscripción Realtime a los INSERT de la bitácora de UNA llamada. Canal por
// llamada seleccionada y filtro en servidor: un canal global mandaría a cada
// panel los eventos de todas las llamadas. Devuelve la función de desuscripción.
// Mismo preámbulo de auth que suscribirLlamadas: Realtime aplica la RLS con el
// token del suscriptor, y sin setAuth el socket va con la anon key (cero
// policies → SUBSCRIBED pero sin eventos).
export function suscribirEventosLlamada(
  callId: string,
  onEvento: (evento: LlamadaEventoPanel) => void
): () => void {
  const canal = supabase.channel(`llamada-eventos-${callId}`).on(
    'postgres_changes',
    {
      event: 'INSERT',
      schema: 'public',
      table: 'llamada_eventos',
      filter: `call_id=eq.${callId}`,
    },
    // `datos` se quita AQUÍ, en ejecución. Realtime NO pasa por el `select` de
    // PostgREST: entrega la fila entera del WAL, y `LlamadaEventoPanel` es un
    // `Omit<>` que solo existe en compilación. Sin esto, un transfer_* llega al
    // navegador con el payload íntegro de Retell —transcripción, URL de
    // grabación y las variables dinámicas con el cliente identificado—, que es
    // justo lo que el select evita en la carga inicial.
    (payload) => onEvento(_sinDatos(payload.new as Record<string, unknown>))
  );

  let cancelado = false;
  void supabase.auth
    .getSession()
    .then(({ data }) => {
      // Si el consumidor se desuscribió antes de resolver getSession, no dejar
      // un canal zombi suscrito sin dueño (misma carrera que en el feed).
      if (cancelado) return;
      if (data.session) supabase.realtime.setAuth(data.session.access_token);
      canal.subscribe();
    })
    .catch((e: unknown) => {
      console.error(JSON.stringify({ evt: 'suscripcion_eventos_failed', message: String(e) }));
    });

  return () => {
    cancelado = true;
    void supabase.removeChannel(canal);
  };
}

// Suscripción Realtime (INSERT + UPDATE sobre public.llamadas) en el canal
// único del panel. Devuelve la función de desuscripción (cleanup del hook,
// idempotente: removeChannel tolera repetirse).
//
// Realtime respeta RLS con el token del SUSCRIPTOR: sin setAuth explícito el
// socket usa la anon key (cero policies → SUBSCRIBED pero sin eventos, visto
// en el smoke del riesgo R1). supabase-js lo re-propaga solo en los refresh
// posteriores; aquí lo fijamos antes de suscribir.
// `onReconectado` (opcional) se dispara cuando el canal vuelve a SUBSCRIBED
// tras un corte: el consumidor relanza su fetch para no perder eventos
// emitidos durante la caída (compromiso de spec §8).
export function suscribirLlamadas(
  onCambio: (llamada: LlamadaRow, evento: EventoLlamada) => void,
  onReconectado?: () => void
): () => void {
  const canal = supabase
    .channel('panel-llamadas')
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'llamadas' }, (payload) =>
      onCambio({ ...(payload.new as LlamadaRow), transcripcion: null, datos_extra: null }, 'INSERT')
    )
    .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'llamadas' }, (payload) =>
      onCambio({ ...(payload.new as LlamadaRow), transcripcion: null, datos_extra: null }, 'UPDATE')
    );

  let yaSuscrito = false;
  let cancelado = false;
  void supabase.auth
    .getSession()
    .then(({ data }) => {
      // si el consumidor se desuscribió antes de resolver getSession, no
      // dejar un canal zombi suscrito sin dueño (carrera del work review)
      if (cancelado) return;
      if (data.session) supabase.realtime.setAuth(data.session.access_token);
      canal.subscribe((estado) => {
        if (estado === 'SUBSCRIBED') {
          if (yaSuscrito) onReconectado?.();
          yaSuscrito = true;
        }
      });
    })
    .catch((e: unknown) => {
      console.error(JSON.stringify({ evt: 'suscripcion_llamadas_failed', message: String(e) }));
    });

  return () => {
    cancelado = true;
    void supabase.removeChannel(canal);
  };
}

// Reconcilia el resultado de un fetch con el estado previo del feed: por `id`
// gana la versión más reciente (actualizado_en). Evita que un fetch en vuelo
// pise eventos realtime llegados mientras cargaba (hallazgo ad:codex P2-1).
export function _reconciliarFeed<T extends LlamadaRow>(filas: T[], previas: T[], dia: string): T[] {
  let resultado = filas;
  for (const p of previas) {
    const f = resultado.find((x) => x.id === p.id);
    if (!f || p.actualizado_en > f.actualizado_en) {
      resultado = _mergeLlamada(resultado, p, dia);
    }
  }
  return resultado;
}

// Upsert por `id` manteniendo orden inicio DESC; descarta filas fuera del día
// visible (incluye inicio null: el feed diario no las muestra). No muta la
// lista de entrada.
export function _mergeLlamada<T extends LlamadaRow>(lista: T[], l: T, dia: string): T[] {
  const { desde, hasta } = _rangoDia(dia);
  // Comparación en epoch: PostgREST serializa timestamptz como +00:00 y
  // toISOString como .000Z — lexicográficamente no son comparables.
  const t = l.inicio === null ? null : Date.parse(l.inicio);
  const dentroDelDia = t !== null && t >= Date.parse(desde) && t < Date.parse(hasta);

  const sinLaFila = lista.filter((x) => x.id !== l.id);
  if (!dentroDelDia) return sinLaFila;

  return [...sinLaFila, l].sort(
    (a, b) => Date.parse(b.inicio ?? '1970-01-01') - Date.parse(a.inicio ?? '1970-01-01')
  );
}
